import { useMemo, useState } from "react";
import { categoryList, feedItems, hotTitles, type FeedItem } from "@/mocks/data";

const detailBody: Record<number, string> = {
  1: "两个账号、两种人生，是近年最常见的反差戏码之一。白天那个热爱生活、说话温柔的账号负责经营形象，深夜这个才是真正卸下伪装后的样子。评论区讨论的焦点并不在事件本身，而在「到底哪一面才是真的」。",
  2: "自习室里永远坐第一排、笔记工整到可以出书的优等生，深夜私聊却用小号疯狂自嘲、连发多个摆烂表情。反差不是作秀，很多时候只是一个人把压力藏进了别人看不见的地方。",
  3: "一封封措辞得体、滴水不漏的商务邮件，很难让人联想到发件人在下班后的同事群里有多放得开。职场人普遍需要一套「官方腔」，可当两副腔调差距过大时，围观者自然会产生好奇。",
  4: "感情里的反差最容易引爆讨论：人前频繁合体、人后互相拆台。当私聊与公开表达出现巨大温差，观众最先追问的往往不是对错，而是「他们到底有没有在认真谈」。",
  5: "网上指点江山、现实缄口不言，几乎是当下最普遍的「反差」。它其实揭示了一个简单的事实：表达和行动之间，永远隔着一道不容易跨过的坎。",
  6: "镜头前乖巧营业，群聊里放飞自我，是不少兴趣圈的真实写照。老粉对这种反差多是「早猜到了」的默契，反而让这件事多了几分圈内圈外的趣味感。",
  7: "职场上雷厉风行的高管，私下却会给自家宠物起充满少女心的昵称。这种反差不制造冲突，反而让人物变得立体可爱，也是少数「反差得让人喜欢」的例子。",
  8: "严肃管理班群的班长，用小号在匿名区吐槽同学，本以为自己切换得天衣无缝，却败给了系统弹出的登录提示音。场面尴尬之余，也提醒我们：台前幕后的距离，可能比想象中要近。",
  9: "朋友圈里永远耐心温柔的好爸爸，私下也会向发小坦诚带娃的崩溃。这个反差最打动人的地方在于，它没有毁掉任何人设，只是让人承认了「坚强的人也会累」。",
};

export default function Feed() {
  const [cat, setCat] = useState<string>("全部");
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<FeedItem | null>(null);

  const items = useMemo(() => {
    return feedItems.filter((it) => {
      const matchCat = cat === "全部" || it.category === cat;
      const kw = keyword.trim().toLowerCase();
      const matchKw =
        !kw ||
        it.title.toLowerCase().includes(kw) ||
        it.summary.toLowerCase().includes(kw) ||
        it.tag.toLowerCase().includes(kw) ||
        it.category.toLowerCase().includes(kw);
      return matchCat && matchKw;
    });
  }, [cat, keyword]);

  return (
    <section id="feed" className="relative w-full bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
        {/* section head */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Fresh Picks</p>
            <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">最新反差盘点</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              按现象类型持续收录对话细节、微信截图与私聊记录素材，帮你快速看懂每条反差线索的完整脉络。
            </p>
          </div>
          <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 md:w-72">
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索现象 / 关键词"
              aria-label="搜索反差内容"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 rounded-lg bg-background-50 p-1.5 ring-1 ring-background-200 md:inline-flex">
          {categoryList.map((c) => {
            const active = cat === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-secondary-100 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_330px]">
          {/* feed cards */}
          <div>
            <div className="flex items-center justify-between border-b border-background-300 pb-2">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                {cat === "全部" ? "全部盘点" : `${cat} · 盘点`}
              </h3>
              <span className="text-xs text-foreground-500">共 {items.length} 条</span>
            </div>

            {items.length === 0 ? (
              <div className="mt-10 flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <i className="ri-inbox-2-line text-3xl text-foreground-400" />
                <p className="text-sm text-foreground-600">没有匹配的内容，换个关键词试试吧。</p>
              </div>
            ) : (
              <ul className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {items.map((it, idx) => (
                  <li
                    key={it.id}
                    className={`group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300 ${
                      idx === 0 ? "sm:col-span-2 sm:flex-row" : ""
                    }`}
                    onClick={() => setSelected(it)}
                  >
                    <div
                      className={`relative w-full overflow-hidden bg-background-200 ${
                        idx === 0 ? "sm:h-auto sm:w-2/5" : "aspect-[4/3]"
                      }`}
                    >
                      <img
                        src={it.image}
                        alt={it.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-foreground-950/85 px-2 py-1 text-xs font-semibold text-background-50">
                        <i className="ri-fire-fill text-primary-400" />
                        {it.hot}
                      </span>
                    </div>
                    <div className={`flex flex-1 flex-col p-4 md:p-5 ${idx === 0 ? "sm:justify-center" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-900">
                          {it.category}
                        </span>
                        <span className="text-xs text-primary-600">{it.tag}</span>
                      </div>
                      <h3
                        className={`mt-2.5 font-heading font-bold leading-snug text-foreground-950 group-hover:text-primary-700 ${
                          idx === 0 ? "text-xl md:text-2xl" : "text-base md:text-lg"
                        }`}
                      >
                        {it.title}
                      </h3>
                      <p
                        className={`mt-2 text-sm leading-relaxed text-foreground-600 ${
                          idx === 0 ? "" : "line-clamp-2"
                        }`}
                      >
                        {it.summary}
                      </p>
                      <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-user-line text-foreground-400" />
                          {it.author}
                        </span>
                        <span className="inline-flex items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-eye-line text-foreground-400" />
                            {it.readCount}
                          </span>
                          <span>{it.date}</span>
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* hot rank */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-fill text-xl text-primary-500" />
                  今日热榜
                </h3>
                <span className="text-xs text-foreground-500">实时更新</span>
              </div>
              <ol className="mt-4 flex flex-col gap-4">
                {hotTitles.map((h, i) => (
                  <li key={h.id} className="flex items-start gap-3 border-b border-background-200 pb-3 last:border-0 last:pb-0">
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-xs font-black ${
                        i < 3 ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <a href="#feed" className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors hover:text-primary-700">
                        {h.text}
                      </a>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {h.views}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-accent-700">
                          <i className="ri-arrow-up-line" />
                          {h.delta}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              <a
                href="#feed"
                className="mt-4 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2.5 text-sm font-semibold text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                查看更多
              </a>
            </div>
          </aside>
        </div>
      </div>

      {/* detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 p-4"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-label={selected.title}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[16/7] w-full overflow-hidden bg-background-200">
              <img src={selected.image} alt={selected.title} className="h-full w-full object-cover object-top" />
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setSelected(null)}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/70 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-close-line text-lg" />
              </button>
              <span className="absolute left-4 top-4 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                {selected.category}
              </span>
            </div>
            <div className="flex flex-col gap-4 overflow-y-auto p-5 md:p-6">
              <h3 className="font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                {selected.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line text-foreground-400" />
                  {selected.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line text-foreground-400" />
                  {selected.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-400" />
                  {selected.readCount} 阅读
                </span>
              </div>
              <p className="text-sm leading-relaxed text-foreground-700">{selected.summary}</p>
              <p className="text-sm leading-relaxed text-foreground-700">
                {detailBody[selected.id] ?? "这是按现有公开信息整理的内容，若事件有新进展，本栏目将持续跟进并修正，建议理性围观、审慎下结论。"}
              </p>
              <p className="rounded-md bg-secondary-100 px-4 py-3 text-xs leading-relaxed text-secondary-900">
                提示：反差雷达聚焦现象与话题本身，内容均基于公开讨论整理。涉及真实个人的敏感素材会做匿名与脱敏处理。
              </p>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="mt-1 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                我已了解，返回列表
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}